import { formatDate } from '@frontend/mwm-ui'
import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { IUAFilters } from './Components/Filters'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { IUADashboardPayload } from '~/api/user-acquisition'

import UAFilters from './Components/Filters'
import Table from './Components/Table'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import Modal from '~/components/Modal'
import ViewTitle from '~/components/ViewTitle'
import { LOCAL_STORAGE } from '~/const'
import { platformOptions } from '~/const/options'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useUADashboardDataQuery } from '~/queries/user-acquisition'
import dayjs from '~/utils/dayjs'

import './styles.scss'

const defaultFilters = (searchParams?: URLSearchParams): IUAFilters => {
  const defaultFilters: IUAFilters = {
    aggregations: [],
    app_key: null,
    country: null,
    device_type: null,
    end_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    start_date: dayjs().subtract(31, 'day').format('YYYY-MM-DD'),
    top_countries_count: null,
    ua_adset_id: null,
    ua_campaign_id: null,
    ua_source: null,
  }

  searchParams?.forEach((value, key) => {
    if (Object.keys(defaultFilters).includes(key)) {
      if (key === 'end_date' || key === 'start_date') {
        defaultFilters[key] = value
      } else if (key === 'device_type') {
        const option = platformOptions.find(({ value: optionValue }) => optionValue === value)
        defaultFilters[key] = option ? option : null
      } else if (key !== 'aggregations') {
        defaultFilters[key] = { label: value, value }
      }
    }
  })

  const filtersFromParams = {
    ...defaultFilters,
    aggregations: searchParams?.getAll('aggregations[]').map((value) => ({ label: value, value })) || [],
    app_key: searchParams?.getAll('app_key[]').map((value) => ({ label: value, value })) || [],
    country: searchParams?.getAll('country[]').map((value) => ({ label: value, value })) || [],
  }

  return filtersFromParams
}

const filtersToPayload = (filters: IUAFilters): IUADashboardPayload => {
  const payloadFilters: { key: string; value?: string; values?: string[] }[] = []

  for (const [key, option] of Object.entries(filters)) {
    if ((key === 'app_key' || key === 'country') && Array.isArray(option)) {
      payloadFilters.push({ key, values: option.map(({ value }) => value) })
    } else if (option?.value && key !== 'top_countries_count') {
      payloadFilters.push({ key, value: option.value })
    }
  }

  if (filters.start_date) {
    payloadFilters.push({ key: 'start_date', value: filters.start_date })
  }

  if (filters.end_date) {
    payloadFilters.push({ key: 'end_date', value: filters.end_date })
  }

  return {
    aggregations: filters.aggregations.map(({ value }) => value),
    filters: payloadFilters,
    top_countries_count: filters.top_countries_count?.value ? parseInt(filters.top_countries_count?.value) : undefined,
  }
}

const filtersToParams = (filters: IUAFilters) => {
  const mappedFilters: Record<string, string> = {}

  for (const [filterKey, filterValue] of Object.entries(filters)) {
    if (filterValue && !Array.isArray(filterValue)) {
      if (typeof filterValue === 'object' && filterValue.hasOwnProperty('value')) {
        mappedFilters[filterKey] = filterValue.value
      } else {
        mappedFilters[filterKey] = filterValue
      }
    }
  }

  const params = new URLSearchParams(mappedFilters)

  for (const [filterKey, filterValue] of Object.entries(filters)) {
    if (Array.isArray(filterValue) && filterValue.length > 0) {
      filterValue.forEach(({ value }) => {
        params.append(`${filterKey}[]`, value)
      })
    }
  }

  return params
}

const UADashboard = (): ReactElement => {
  /** Local state */

  const gridRef = useRef<AgGridReact>(null)

  const modalGridRef = useRef<AgGridReact>(null)

  const [isFullScreen, setIsFullScreen] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, setFilters] = useState(defaultFilters(searchParams))

  const [payload, setCurrentPayload] = useState<IUADashboardPayload>()

  /** Queries & Mutations */

  const { data: uaDashboardData, isFetching } = useUADashboardDataQuery(payload)

  /** Effects */

  useDocumentTitle('UA Dashboard')

  useEffect(() => {
    setSearchParams(filtersToParams(filters), { replace: true })
  }, [filters, setSearchParams])

  /** Handlers */

  const handleFilterChanged = (key: keyof IUAFilters, value: IOption | IOption[] | null) => {
    setFilters((prev) => ({
      ...prev,
      [key]: (key === 'app_key' || key === 'country') && value && !Array.isArray(value) ? [value] : value,
    }))
  }

  const handleDateChanged = (dateRange: string[]) => {
    const [start_date, end_date] = dateRange
    setFilters((prev) => ({
      ...prev,
      end_date: formatDate(end_date, 'YYYY-MM-DD'),
      start_date: formatDate(start_date, 'YYYY-MM-DD'),
    }))
  }

  const handleSubmit = () => setCurrentPayload(filtersToPayload(filters))

  const handleResetFilters = () => {
    setFilters(defaultFilters())
    localStorage.removeItem(LOCAL_STORAGE.UA_DASHBOARD_COLUMNS)
  }

  const handleFullScreen = (isFullScreen: boolean) => () => setIsFullScreen(isFullScreen)

  /** Render */

  return (
    <Layout className="ua-dashboard-view" withPadding>
      <ViewTitle className="layout-view-title">UA Dashboard</ViewTitle>
      <UAFilters
        filters={filters}
        isFullScreen={isFullScreen}
        isSubmitButtonDisabled={isFetching}
        onDateChanged={handleDateChanged}
        onFilterChanged={handleFilterChanged}
        onFullScreen={handleFullScreen(true)}
        onReset={handleResetFilters}
        onSubmit={handleSubmit}
      />
      <ContentLayout>
        {isFullScreen ? (
          <Modal className="ua-dashboard-modal" isOpen={isFullScreen} onCloseModal={handleFullScreen(false)}>
            <UAFilters
              filters={filters}
              isFullScreen={isFullScreen}
              isSubmitButtonDisabled={isFetching}
              onDateChanged={handleDateChanged}
              onFilterChanged={handleFilterChanged}
              onFullScreen={handleFullScreen(true)}
              onReset={handleResetFilters}
              onSubmit={handleSubmit}
            />
            <Table
              dashboardData={uaDashboardData}
              gridRef={modalGridRef}
              isFullScreen={isFullScreen}
              isLoading={isFetching}
            />
          </Modal>
        ) : (
          <Table dashboardData={uaDashboardData} gridRef={gridRef} isFullScreen={isFullScreen} isLoading={isFetching} />
        )}
      </ContentLayout>
    </Layout>
  )
}

export default UADashboard
